"use client";

import { useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  MenuItem,
  IconButton,
} from "@mui/material";
import { Search, Save, Delete, Add } from "@mui/icons-material";
import moment from "moment";

const BulkProductReplacementPanel = () => {
  const [date, setDate] = useState({
    startDate: moment().clone().startOf("month").format("YYYY-MM-DD"),
    endDate: new Date().toISOString().slice(0, 10),
  });
  const [orderId, setOrderId] = useState("");
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Track modified products
  const [modifiedProducts, setModifiedProducts] = useState({});

  // Fetch order details
  const handleOrderSearch = async () => {
    if (!orderId.trim()) return;
    setLoading(true);
    setError("");
    setModifiedProducts({});

    try {
      const response = await axios.post(
        process.env.REACT_APP_PATH_BULK + `/products/stageWiseBulkOrders`,
        {
          startDate: date.startDate,
          endDate: date.endDate,
          numericOrderId: orderId,
          orderStageV2: "all",
        }
      );
      if (response.data.success) {
        setOrderDetails(response.data?.data?.orders[0]);
      } else {
        setError(response.data.message);
        setOrderDetails(null);
      }
    } catch (err) {
      console.error("Error fetching order:", err);
      setError(
        "Failed to fetch order details. Please check the order ID and try again."
      );
      setOrderDetails(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle EAN code change
  const handleEANChange = (productId, newValue, product) => {
    setModifiedProducts((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        newEANCode: newValue,
        isModified: true,
        oldEanCode: product.EANCode,
      },
    }));
  };

  // Check if any products have been modified
  const hasModifiedProducts = () => {
    return Object.values(modifiedProducts).some(
      (product) => product.isModified && product.newEANCode.trim() !== ""
    );
  };

  // Submit all product replacements
  const handleSubmitChanges = async () => {
    if (!hasModifiedProducts()) return;

    setSubmitting(true);

    try {
      const changedProducts = Object.entries(modifiedProducts)
        .filter(
          ([_, product]) =>
            product.isModified && product.newEANCode.trim() !== ""
        )
        .map(([id, product]) => ({
          oldEanCode: product.oldEanCode,
          newEanCode: product.newEANCode,
        }));
      axios
        .post(process.env.REACT_APP_PATH_BULK + "/products/updateSkuOfBulkOrders", {
          orderId: orderDetails._id,
          skus: changedProducts,
        })
        .then((res) => {
          if (res.data.success) {
            // Show success message
            alert(res.data?.message || "Successfully updated");
            handleOrderSearch();

            // Reset modified products
            setModifiedProducts({});
          } else {
            alert(res.data?.message || "Someting went wrong");
          }
        })
        .catch((error) => {
          alert("Someting went wrong");
        });
    } catch (err) {
      console.error("Error replacing products:", err);
      alert("Someting went wrong");
      // setSnackbar({
      //   open: true,
      //   message: "Failed to replace products. Please try again.",
      //   severity: "error",
      // });
    } finally {
      setSubmitting(false);
    }
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // --- Remove SKU & redistribute its quantity across other SKUs ---
  const [removeEanCode, setRemoveEanCode] = useState("");
  const [redistributeTargets, setRedistributeTargets] = useState([
    { eanCode: "", quantity: "" },
  ]);
  const [redistributing, setRedistributing] = useState(false);

  const removedProduct = orderDetails?.products?.find(
    (p) => p.EANCode === removeEanCode
  );
  const removedQty = removedProduct?.quantity || 0;
  const redistributedQtySum = redistributeTargets.reduce(
    (sum, t) => sum + (parseInt(t.quantity, 10) || 0),
    0
  );
  const qtyMatches = removeEanCode !== "" && redistributedQtySum === removedQty;

  const handleRedistributeTargetChange = (index, field, value) => {
    setRedistributeTargets((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addRedistributeTargetRow = () => {
    setRedistributeTargets((prev) => [...prev, { eanCode: "", quantity: "" }]);
  };

  const removeRedistributeTargetRow = (index) => {
    setRedistributeTargets((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveEanCodeSelect = (value) => {
    setRemoveEanCode(value);
    setRedistributeTargets([{ eanCode: "", quantity: "" }]);
  };

  const handleSubmitRedistribution = async () => {
    if (!removeEanCode || !qtyMatches) return;

    const targets = redistributeTargets
      .filter((t) => t.eanCode.trim() !== "" && parseInt(t.quantity, 10) > 0)
      .map((t) => ({ eanCode: t.eanCode.trim(), quantity: parseInt(t.quantity, 10) }));

    if (targets.length === 0) return;

    setRedistributing(true);
    try {
      const res = await axios.post(
        process.env.REACT_APP_PATH_BULK + "/products/redistributeSkuQuantityInBulkOrder",
        {
          orderId: orderDetails._id,
          removeEanCode,
          targets,
        }
      );
      if (res.data.success) {
        alert(res.data?.message || "Successfully updated");
        setRemoveEanCode("");
        setRedistributeTargets([{ eanCode: "", quantity: "" }]);
        handleOrderSearch();
      } else {
        alert(res.data?.message || "Something went wrong");
      }
    } catch (err) {
      console.error("Error redistributing SKU quantity:", err);
      alert("Something went wrong");
    } finally {
      setRedistributing(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Product Replacement in Order
        </Typography>

        {/* Order Search Form */}
        <Box component="div" sx={{ display: "flex", mb: 3 }}>
          <TextField
            label="Order ID"
            variant="outlined"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            sx={{ flexGrow: 1, mr: 2 }}
            placeholder="Enter order ID"
            required
          />
          <Button
            type="submit"
            variant="contained"
            startIcon={<Search />}
            disabled={loading}
            onClick={handleOrderSearch}
          >
            {loading ? <CircularProgress size={24} /> : "Search"}
          </Button>
        </Box>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {/* Order Details */}
        {orderDetails && (
          <>
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Order Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Customer Name
                    </Typography>
                    <Typography variant="body1">{orderDetails.name}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Phone Number
                    </Typography>
                    <Typography variant="body1">
                      {orderDetails.phoneNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Created At
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(orderDetails.createdAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Amount
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {orderDetails?.totalPayableAmount.toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Products Table with Editable EAN Codes */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">Order Products</Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Save />}
                disabled={submitting || !hasModifiedProducts()}
                onClick={handleSubmitChanges}
              >
                {submitting ? <CircularProgress size={24} /> : "Submit Changes"}
              </Button>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="center">Quantity</TableCell>
                    <TableCell>Current EAN Code</TableCell>
                    <TableCell>New EAN Code</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderDetails.products.map((product) => (
                    <TableRow key={product.productId}>
                      <TableCell>{product.productId}</TableCell>
                      <TableCell>{product.name}</TableCell>
                      <TableCell align="center">{product.quantity}</TableCell>
                      <TableCell>{product.EANCode}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="Enter new EAN code"
                          value={
                            modifiedProducts[product.productId]?.newEANCode ||
                            ""
                          }
                          onChange={(e) =>
                            handleEANChange(
                              product.productId,
                              e.target.value,
                              product
                            )
                          }
                          variant="outlined"
                          fullWidth
                          error={
                            modifiedProducts[product.productId]?.newEANCode ===
                            ""
                          }
                          helperText={
                            modifiedProducts[product.productId]?.newEANCode ===
                            ""
                              ? "EAN code cannot be empty"
                              : ""
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Remove SKU & redistribute quantity */}
            <Box sx={{ mt: 5 }}>
              <Typography variant="h6" gutterBottom>
                Remove SKU & Redistribute Quantity
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Pick a SKU to remove entirely, then split its quantity across other
                SKUs (existing in this order, or new) that share the same MRP. The
                quantities you enter below must add up exactly to the removed SKU's
                quantity so the total payable amount stays unchanged.
              </Typography>

              <TextField
                select
                label="SKU to remove"
                value={removeEanCode}
                onChange={(e) => handleRemoveEanCodeSelect(e.target.value)}
                size="small"
                sx={{ minWidth: 320, mb: 2 }}
              >
                <MenuItem value="">
                  <em>Select a SKU</em>
                </MenuItem>
                {orderDetails.products.map((p) => (
                  <MenuItem key={p.EANCode} value={p.EANCode}>
                    {p.name} ({p.EANCode}) — qty {p.quantity}
                  </MenuItem>
                ))}
              </TextField>

              {removeEanCode && (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Removing <strong>{removedProduct?.name}</strong> — quantity to
                    redistribute: <strong>{removedQty}</strong>
                  </Typography>

                  {redistributeTargets.map((t, index) => (
                    <Box
                      key={index}
                      sx={{ display: "flex", gap: 2, mb: 1, alignItems: "center" }}
                    >
                      <TextField
                        size="small"
                        label="Target EAN code"
                        placeholder="Existing or new SKU's EAN code"
                        value={t.eanCode}
                        onChange={(e) =>
                          handleRedistributeTargetChange(
                            index,
                            "eanCode",
                            e.target.value
                          )
                        }
                        sx={{ flexGrow: 1 }}
                      />
                      <TextField
                        size="small"
                        label="Quantity"
                        type="number"
                        value={t.quantity}
                        onChange={(e) =>
                          handleRedistributeTargetChange(
                            index,
                            "quantity",
                            e.target.value
                          )
                        }
                        sx={{ width: 140 }}
                      />
                      <IconButton
                        onClick={() => removeRedistributeTargetRow(index)}
                        disabled={redistributeTargets.length === 1}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  ))}

                  <Button
                    startIcon={<Add />}
                    onClick={addRedistributeTargetRow}
                    sx={{ mb: 2 }}
                  >
                    Add another SKU
                  </Button>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography
                      variant="body2"
                      color={qtyMatches ? "success.main" : "error"}
                    >
                      {redistributedQtySum} / {removedQty} quantity allocated
                      {!qtyMatches && " — must match exactly"}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<Save />}
                      disabled={!qtyMatches || redistributing}
                      onClick={handleSubmitRedistribution}
                    >
                      {redistributing ? (
                        <CircularProgress size={24} />
                      ) : (
                        "Submit Redistribution"
                      )}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </>
        )}
      </Paper>

      {/* Success/Error Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BulkProductReplacementPanel;
